import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { TERMS_OF_SERVICE_VERSION } from '@/constants/legal';
import { useResidentProperty } from '@/hooks/useResidentProperty';
import {
  getConsentHistory,
  getNotificationPreferences,
  recordConsent,
  updateNotificationPreferences,
} from '@/services/api.client';
import type { NotificationPreferences, WalkinNotifyMode } from '@/types';
import { TrustedOperatorAlerts } from './TrustedOperatorAlerts';

// RES-W-08 — Notification Preferences.
// Reached from AccountPage or directly via /resident/account/notifications.
// Persists immediately on toggle (optimistic updates via react-query mutation).

interface ToggleRowProps {
  id: string;
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}

function ToggleRow({ id, title, description, checked, disabled, onChange }: ToggleRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <div className="flex-1">
        <Label htmlFor={id} className="text-sm font-medium leading-none">
          {title}
        </Label>
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        aria-label={title}
      />
    </div>
  );
}

export function NotificationPreferencesPage() {
  const qc = useQueryClient();
  const property = useResidentProperty();
  const propertyId = property?.propertyId ?? '';

  const query = useQuery({
    queryKey: ['notification-preferences', propertyId],
    queryFn: () => getNotificationPreferences(propertyId),
    enabled: propertyId !== '',
  });

  const mutation = useMutation({
    mutationFn: (patch: Partial<NotificationPreferences>) =>
      updateNotificationPreferences(propertyId, patch),
    onSuccess: (updated) => {
      qc.setQueryData<NotificationPreferences>(['notification-preferences', propertyId], updated);
    },
  });

  // Marketing email is a consent record, not an operational preference.
  // KDPA §32 — without an explicit opt-in row in `consent_events` with
  // `granted=true`, we have no lawful basis to send marketing.
  const marketingQuery = useQuery({
    queryKey: ['consent-history', 'marketing_email'],
    queryFn: () => getConsentHistory('marketing_email'),
  });

  const marketingOptedIn =
    (marketingQuery.data ?? []).slice().sort((a, b) => b.created_at.localeCompare(a.created_at))[0]
      ?.granted === true;

  const marketingMutation = useMutation({
    mutationFn: (granted: boolean) =>
      recordConsent({
        scope: 'marketing_email',
        version: TERMS_OF_SERVICE_VERSION,
        granted,
        propertyId,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['consent-history', 'marketing_email'] });
    },
  });

  const prefs = query.data;

  if (!property) {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <div>
          <h1 className="text-2xl">Notification preferences</h1>
          <p className="text-sm text-muted-foreground">
            We couldn&apos;t determine your property. Please re-open this page from your account.
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to="/resident/account">Back to account</Link>
        </Button>
      </div>
    );
  }

  if (query.isPending) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (query.isError || !prefs) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <h1 className="text-2xl">Notification preferences</h1>
        <p className="text-sm text-destructive">
          Couldn&apos;t load your preferences. Please refresh and try again.
        </p>
        <Button variant="outline" onClick={() => void query.refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const update = (patch: Partial<NotificationPreferences>) => mutation.mutate(patch);

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl">Notification preferences</h1>
        <p className="text-sm text-muted-foreground">
          Choose what you&apos;re notified about and when.
        </p>
        {mutation.isError && (
          <p className="mt-2 text-sm text-destructive" role="alert">
            Couldn&apos;t save that change. Please try again.
          </p>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Visitor activity</CardTitle>
          <CardDescription>
            Tell me when my visitors arrive, leave, or skip a visit.
          </CardDescription>
        </CardHeader>
        <CardContent className="divide-y divide-border">
          <ToggleRow
            id="notifyVisitorCheckin"
            title="Visitor check-in"
            description="Notify me when a visitor I invited arrives at the gate."
            checked={prefs.notifyVisitorCheckin}
            disabled={mutation.isPending}
            onChange={(v) => update({ notifyVisitorCheckin: v })}
          />
          <ToggleRow
            id="notifyVisitorCheckout"
            title="Visitor check-out"
            description="Notify me when a visitor leaves."
            checked={prefs.notifyVisitorCheckout}
            disabled={mutation.isPending}
            onChange={(v) => update({ notifyVisitorCheckout: v })}
          />
          <ToggleRow
            id="notifyAutoClose"
            title="Auto-closed visits"
            description="Notify me when a visit is auto-closed because check-out wasn't recorded."
            checked={prefs.notifyAutoClose}
            disabled={mutation.isPending}
            onChange={(v) => update({ notifyAutoClose: v })}
          />
          <ToggleRow
            id="notifyDeliveryArrival"
            title="Delivery arrivals"
            description="Notify me when a delivery arrives at the gate."
            checked={prefs.notifyDeliveryArrival}
            disabled={mutation.isPending}
            onChange={(v) => update({ notifyDeliveryArrival: v })}
          />
          <ToggleRow
            id="notifyDomesticWorkerAbsence"
            title="Domestic worker absence"
            description="Notify me when my domestic worker doesn't arrive on a scheduled day."
            checked={prefs.notifyDomesticWorkerAbsence}
            disabled={mutation.isPending}
            onChange={(v) => update({ notifyDomesticWorkerAbsence: v })}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Visitors at the gate</CardTitle>
          <CardDescription>
            When someone arrives without an invitation and asks to see you, how should we reach out?
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="walkinMode">When a visitor walks in</Label>
            <Select
              value={prefs.walkinMode}
              onValueChange={(v) => update({ walkinMode: v as WalkinNotifyMode })}
              disabled={mutation.isPending}
            >
              <SelectTrigger id="walkinMode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="always">Always ask me</SelectItem>
                <SelectItem value="scheduled_hours">Only during set hours</SelectItem>
                <SelectItem value="auto_approve_from_favourites">
                  Let my favourites in automatically, ask for everyone else
                </SelectItem>
                <SelectItem value="dnd">Don't ask me — turn them away</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {prefs.walkinMode === 'scheduled_hours' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="walkinHoursFrom">From</Label>
                <Input
                  id="walkinHoursFrom"
                  type="time"
                  value={prefs.walkinHoursFrom ?? ''}
                  disabled={mutation.isPending}
                  onChange={(e) => update({ walkinHoursFrom: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="walkinHoursUntil">Until</Label>
                <Input
                  id="walkinHoursUntil"
                  type="time"
                  value={prefs.walkinHoursUntil ?? ''}
                  disabled={mutation.isPending}
                  onChange={(e) => update({ walkinHoursUntil: e.target.value })}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Quiet hours</CardTitle>
          <CardDescription>
            Silence non-urgent notifications during a window — usually overnight.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ToggleRow
            id="dndEnabled"
            title="Turn on quiet hours"
            description="Hold non-urgent notifications during the times you set below."
            checked={prefs.dndEnabled}
            disabled={mutation.isPending}
            onChange={(v) => update({ dndEnabled: v })}
          />
          {prefs.dndEnabled && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dndFrom">From</Label>
                <Input
                  id="dndFrom"
                  type="time"
                  value={prefs.dndFrom ?? ''}
                  disabled={mutation.isPending}
                  onChange={(e) => update({ dndFrom: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dndUntil">Until</Label>
                <Input
                  id="dndUntil"
                  type="time"
                  value={prefs.dndUntil ?? ''}
                  disabled={mutation.isPending}
                  onChange={(e) => update({ dndUntil: e.target.value })}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Community</CardTitle>
          <CardDescription>Property-wide messages from management.</CardDescription>
        </CardHeader>
        <CardContent>
          <ToggleRow
            id="notifyAnnouncements"
            title="Announcements"
            description="Receive property announcements from your property manager."
            checked={prefs.notifyAnnouncements}
            disabled={mutation.isPending}
            onChange={(v) => update({ notifyAnnouncements: v })}
          />
        </CardContent>
      </Card>

      {propertyId !== '' && <TrustedOperatorAlerts propertyId={propertyId} />}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">From Secure Labs</CardTitle>
          <CardDescription>
            Optional updates from us about new features and tips. Off by default — switch on if
            you&apos;d like to hear from us.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ToggleRow
            id="marketingEmailOptIn"
            title="Product updates by email"
            description="Occasional emails from Secure Labs (Secure Labs Limited) about new features, tips, and product news. You can switch this off at any time."
            checked={marketingOptedIn}
            disabled={marketingQuery.isPending || marketingMutation.isPending}
            onChange={(v) => marketingMutation.mutate(v)}
          />
          {marketingMutation.isError && (
            <p className="mt-2 text-xs text-destructive" role="alert">
              Couldn&apos;t save that preference. Please try again.
            </p>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-start">
        <Button asChild variant="outline">
          <Link to="/resident/account">Back to account</Link>
        </Button>
      </div>
    </div>
  );
}
